import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { MouseEvent, MouseEventHandler, ReactNode } from 'react';
import { useEffect } from 'react';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs';
import { FOOTBALL } from '@sorare/core/src/constants/__generated__/routes';
import { useCloseDialog } from '@sorare/core/src/hooks/navigation/useCloseDialog';
import { useDialogParam } from '@sorare/core/src/hooks/navigation/useDialogParam';
import { useDeeplinkFromNativeApp } from '@sorare/core/src/hooks/useDeeplinkFromNativeApp';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import { MonetaryAmount } from '@sorare/core/src/lib/monetaryAmount';
import type { ClickInstantBuy_Flow } from '@sorare/core/src/protos/events/so5/shared/events';
import {
  Collection,
  rarityFromJSON,
} from '@sorare/core/src/protos/webview/channel/native_messages';

import { PrimaryOfferPrice } from '#src/components/primaryBuy/PrimaryBuyBuyField/Price/index.tsx';
import type { PrimaryBuyConfirmationOptions } from '#src/contexts/buyingConfirmation/index.tsx';
import { useBuyConfirmationContext } from '#src/contexts/buyingConfirmation/index.tsx';

import type { MobileAppPrimaryOfferBuyFieldWrapper_anyCard } from './__generated__/index.graphql.ts';

function collectionFromSport(sport: Sport): Collection {
  switch (sport) {
    case Sport.BASEBALL:
      return Collection.BASEBALL;
    case Sport.FOOTBALL:
      return Collection.FOOTBALL;
    case Sport.NBA:
      return Collection.NBA;
    default:
      return Collection.COLLECTION_UNKNOWN;
  }
}

export type Props = {
  card: MobileAppPrimaryOfferBuyFieldWrapper_anyCard;
  primaryBuyConfirmationOptions?: PrimaryBuyConfirmationOptions;
  origin: ClickInstantBuy_Flow;
  onClick?: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  gameSlug?: string;
  onBuyConfirmationClose?: () => void;
  composeSessionId?: string;
  children: (props: {
    onClick: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
    loading: boolean;
    disabled?: boolean;
  }) => ReactNode;
};

export const MobileAppPrimaryOfferBuyFieldWrapper = ({
  children,
  card,
  origin,
  gameSlug,
  onBuyConfirmationClose,
  primaryBuyConfirmationOptions,
  onClick,
  composeSessionId = '',
}: Props): ReactNode => {
  const {
    showBuyingConfirmation,
    setShowBuyingConfirmation,
    setBuyConfirmationProps,
  } = useBuyConfirmationContext();
  const {
    flags: {
      androidEnableInstantBuy = false,
      buyOnWebMarketplaceFromAndroid = false,
    },
  } = useFeatureFlags();
  const { postOpenCurrentUrlWithParamsMessage } = useIsMobileApp();

  const { navigateWithDeeplink } = useDeeplinkFromNativeApp();
  const track = useEvents();
  const { toMonetaryAmount } = useMonetaryAmount();
  const { isAndroidApp, isIosApp, postMessage, canUseIosPostMessageAndForYou } =
    useIsMobileApp();
  const idParam = useDialogParam(DialogKey.primaryOfferBought);
  const closeDialog = useCloseDialog();
  useEffect(() => {
    if (
      idParam &&
      card.livePrimaryOffer?.id === idParam &&
      isAndroidApp &&
      !showBuyingConfirmation
    ) {
      setShowBuyingConfirmation(true);
    }
  }, [
    idParam,
    card.livePrimaryOffer?.id,
    isAndroidApp,
    showBuyingConfirmation,
    setShowBuyingConfirmation,
  ]);

  if (!card.livePrimaryOffer?.price) return null;

  const { sport, livePrimaryOffer } = card;
  const { id, price } = livePrimaryOffer;
  const priceMonetaryAmount =
    (price && toMonetaryAmount(price)) || MonetaryAmount.zero;

  const canOpenBrowserFromAndroid =
    buyOnWebMarketplaceFromAndroid && postOpenCurrentUrlWithParamsMessage;

  const disabled =
    (isAndroidApp &&
      canOpenBrowserFromAndroid &&
      (!card.livePrimaryOffer?.googlePrice || !androidEnableInstantBuy)) ||
    (isIosApp && !card.livePrimaryOffer?.appleProductId);

  const buyWithNativeApp = (
    e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>
  ) => {
    if (disabled) return;
    onClick?.(e);
    track('Click Instant Buy', {
      offerId: id,
      cardSlug: card.slug,
      ethAmount: priceMonetaryAmount.eth,
      eurAmount: priceMonetaryAmount.eur,
      gameSlug,
      origin,
      scarcity: card.rarityTyped,
      sport,
      composeSessionId,
    });
    if (
      isAndroidApp &&
      androidEnableInstantBuy &&
      card.livePrimaryOffer?.googlePrice
    ) {
      setBuyConfirmationProps({
        primaryBuyId: card.livePrimaryOffer?.id,
        onClose: () => {
          onBuyConfirmationClose?.();
        },
        beforeOnClose: () => {
          closeDialog(DialogKey.primaryOfferBought);
        },
        primaryBuyConfirmationOptions,
      });
      postMessage('instantBuy', {
        cardSlug: card.slug,
        playerSlug: card.anyPlayer.slug,
        collection: collectionFromSport(card.sport),
        rarity: rarityFromJSON(card.rarityTyped.toUpperCase()),
      });
    } else if (canUseIosPostMessageAndForYou) {
      postMessage('instantBuy', {
        cardSlug: card.slug,
        playerSlug: card.anyPlayer.slug,
        collection: collectionFromSport(card.sport),
        rarity: rarityFromJSON(card.rarityTyped.toUpperCase()),
      });
    } else {
      navigateWithDeeplink(FOOTBALL, undefined, { cardSlug: card.slug });
    }
  };

  return children({
    onClick: buyWithNativeApp,
    loading: false,
    disabled,
  });
};

MobileAppPrimaryOfferBuyFieldWrapper.fragments = {
  anyCard: gql`
    fragment MobileAppPrimaryOfferBuyFieldWrapper_anyCard on AnyCardInterface {
      slug
      sport
      rarityTyped
      anyPlayer {
        slug
      }
      livePrimaryOffer {
        id
        appleProductId @include(if: $isIosApp)
        ...PrimaryOfferPrice_primaryOffer
      }
    }
    ${/* oxlint-disable-next-line sorare/no-detached-fragments */ ''}
    ${PrimaryOfferPrice.fragments.primaryOffer}
  ` as TypedDocumentNode<MobileAppPrimaryOfferBuyFieldWrapper_anyCard>,
};
